import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import auditService from '../../services/auditService';
import { AuditDetail as AuditDetailType } from '../../types/audit.types';
import { formatDateTime } from '../../utils/dateUtils';

const AuditDetail: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [detalle, setDetalle] = useState<AuditDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    loadAuditoria();
  }, [id]);

  const loadAuditoria = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await auditService.getById(Number(id));
      setDetalle(response);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar auditoría');
      console.error('Error cargando auditoría:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);
      setDownloadError(null);
      await auditService.downloadPDF(Number(id));
    } catch (err: any) {
      setDownloadError(err.response?.data?.error || 'Error al descargar PDF');
      console.error('Error descargando PDF:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloading(true);
      setDownloadError(null);
      await auditService.downloadExcel(Number(id));
    } catch (err: any) {
      setDownloadError(err.response?.data?.error || 'Error al descargar Excel');
      console.error('Error descargando Excel:', err);
    } finally {
      setDownloading(false);
    }
  };
  
  const auditoria = detalle?.audit;
  const respuestas = detalle?.responses || [];

  // Helper para convertir valor_respuesta a string mostrable
  const formatRespuesta = (valor: any): string => {
    if (valor === null || valor === undefined) return 'Sin respuesta';

    // Si es un array de objetos (opciones múltiples)
    if (Array.isArray(valor)) {
      return valor.map((v) => formatRespuesta(v)).join(', ');
    }

    // Si es un objeto JSONB con estructura variable
    if (typeof valor === 'object') {
      // Opción múltiple: {opciones: [...]}
      if (valor.opciones && Array.isArray(valor.opciones)) {
        return valor.opciones.join(', ');
      }
      // Prioridad: respuesta > opcion > valor > texto > fecha > hora > label > value
      if (valor.respuesta !== undefined && valor.respuesta !== null) {
        if (typeof valor.respuesta === 'boolean') return valor.respuesta ? 'Sí' : 'No';
        return String(valor.respuesta);
      }
      if (valor.opcion !== undefined && valor.opcion !== null) return String(valor.opcion);
      if (valor.valor !== undefined && valor.valor !== null) return String(valor.valor);
      if (valor.texto !== undefined && valor.texto !== null) return String(valor.texto);
      if (valor.fecha !== undefined && valor.fecha !== null) {
        // Formatear fecha a DD/MM/YYYY
        const d = new Date(valor.fecha);
        if (!isNaN(d.getTime())) {
          const day = String(d.getDate()).padStart(2, '0');
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const year = d.getFullYear();
          return `${day}/${month}/${year}`;
        }
        return String(valor.fecha);
      }
      if (valor.hora !== undefined && valor.hora !== null) {
        // Mostrar hora en formato 24h (sin am/pm)
        const horaStr = String(valor.hora);
        const match = horaStr.match(/^(\d{1,2}):(\d{2})$/);
        if (match) {
          let h = match[1].padStart(2, '0');
          let m = match[2];
          return `${h}:${m}`;
        }
        return horaStr;
      }
      if (valor.label !== undefined && valor.label !== null) return String(valor.label);
      if (valor.value !== undefined && valor.value !== null) return String(valor.value);
      // Si no tiene ninguna propiedad conocida, devolver JSON
      return JSON.stringify(valor);
    }

    if (typeof valor === 'boolean') return valor ? 'Sí' : 'No';
    return String(valor);
  };

  // Helper para detectar respuestas cortas que deben ir en mayúsculas
  const isShortAnswer = (answer: string): boolean => {
    const shortAnswers = ['sí', 'no', 'si', 'excelente', 'bueno', 'regular', 'malo', 'n/a', 'na'];
    return shortAnswers.includes(answer.toLowerCase()) || answer.length <= 3;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const tz = detalle?.audit?.business_timezone;
    return formatDateTime(dateString, tz);
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'completed':
        return 'success';
      case 'draft':
        return 'warning';
      case 'in_progress':
        return 'info';
      default:
        return 'default';
    }
  };

  const getEstadoLabel = (estado: string) => {
    switch (estado) {
      case 'completed':
        return 'Completada';
      case 'draft':
        return 'Borrador';
      case 'in_progress':
        return 'En Progreso';
      default:
        return estado;
    }
  };

  // Agrupar respuestas por categoría
  const respuestasPorCategoria = respuestas?.reduce((acc, respuesta) => {
    const categoria = (respuesta as any).category_name || 'Sin categoría';
    if (!acc[categoria]) {
      acc[categoria] = [];
    }
    acc[categoria].push(respuesta);
    return acc;
  }, {} as Record<string, any[]>);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !auditoria) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'No se pudo cargar la auditoría'}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => navigate('/audits')}
        >
          Volver a auditorías
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: isMobile ? 2 : 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/audits')}
          sx={{ mb: 2 }}
        >
          Volver a auditorías
        </Button>

        <Paper sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Información principal */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h4" gutterBottom>
                    Auditoría #{auditoria.id}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {(auditoria as any).form_name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={getEstadoLabel((auditoria as any).status)}
                    color={getEstadoColor((auditoria as any).status) as any}
                  />
                  {!(auditoria as any).synchronized && (
                    <Chip
                      label="Pendiente sincronización"
                      color="warning"
                      size="small"
                    />
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Datos del negocio y auditor */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <BusinessIcon color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Negocio
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {(auditoria as any).business_name}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PersonIcon color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Auditor
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {(auditoria as any).auditor_name}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Fechas */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon color="action" fontSize="small" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Fecha de inicio
                  </Typography>
                  <Typography variant="body2">
                    {formatDate((auditoria as any).start_date)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {(auditoria as any).end_date && (
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Fecha de completado
                    </Typography>
                    <Typography variant="body2">
                      {formatDate((auditoria as any).end_date)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Puntuación */}
            {(auditoria as any).compliance_percentage !== null && (auditoria as any).compliance_percentage !== undefined && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Cumplimiento General
                  </Typography>
                  <Typography variant="h2" color="primary" fontWeight="bold">
                    {Number((auditoria as any).compliance_percentage).toFixed(1)}%
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Observaciones generales */}
            {(auditoria as any).general_observations && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Observaciones Generales
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {(auditoria as any).general_observations}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Botones de descarga de reportes */}
        {(auditoria as any).status === 'completed' && (
          <Paper sx={{ p: 2, mt: 2 }}>
            {downloadError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDownloadError(null)}>
                {downloadError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Reportes
              </Typography>
              <Button
                variant="contained"
                startIcon={<PdfIcon />}
                onClick={handleDownloadPDF}
                disabled={downloading}
                color="error"
              >
                Descargar PDF
              </Button>
              <Button
                variant="contained"
                startIcon={<ExcelIcon />}
                onClick={handleDownloadExcel}
                disabled={downloading}
                color="success"
              >
                Exportar Excel
              </Button>
            </Box>
            {downloading && (
              <Box sx={{ mt: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2, display: 'inline' }}>
                  Generando reporte...
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </Box>

      {/* Respuestas del Formulario */}
      {respuestasPorCategoria && Object.keys(respuestasPorCategoria).length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Detalle de Respuestas
          </Typography>

          {Object.entries(respuestasPorCategoria).map(([categoria, respuestas]) => (
            <Card 
              key={categoria} 
              sx={{ 
                mb: 3, 
                boxShadow: 2,
                '&:hover': { boxShadow: 4 },
                transition: 'box-shadow 0.3s'
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ 
                  bgcolor: 'primary.main', 
                  color: 'primary.contrastText', 
                  p: 2 
                }}>
                  <Typography variant="h6" fontWeight="600">
                    {categoria}
                  </Typography>
                </Box>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 600, width: isMobile ? '40%' : '35%' }}>
                          Pregunta
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, width: isMobile ? '60%' : '25%' }}>
                          Respuesta
                        </TableCell>
                        {!isMobile && (
                          <TableCell sx={{ fontWeight: 600, width: '25%' }}>
                            Observaciones
                          </TableCell>
                        )}
                        <TableCell align="center" sx={{ fontWeight: 600, width: '15%' }}>
                          Evidencias
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {respuestas.map((respuesta, index) => (
                        <TableRow 
                          key={respuesta.id || index}
                          sx={{
                            '&:nth-of-type(odd)': { bgcolor: 'background.default' },
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {(respuesta as any).question_text}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 500,
                                color: 'text.primary',
                                textTransform: isShortAnswer(formatRespuesta((respuesta as any).response_value)) ? 'uppercase' : 'none',
                                letterSpacing: isShortAnswer(formatRespuesta((respuesta as any).response_value)) ? '0.5px' : 'normal',
                              }}
                            >
                              {formatRespuesta((respuesta as any).response_value)}
                            </Typography>
                          </TableCell>
                          {!isMobile && (
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: (respuesta as any).comment ? 'normal' : 'italic' }}>
                                {(respuesta as any).comment || 'Sin observaciones'}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                            {(respuesta as any).evidences && (respuesta as any).evidences.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                                <Chip 
                                  icon={<ImageIcon />}
                                  label={(respuesta as any).evidences.length}
                                  size="small"
                                  color="success"
                                  sx={{ minWidth: 60 }}
                                />
                                {(respuesta as any).evidences.map((evidencia: any, idx: number) => (
                                  <Button
                                    key={idx}
                                    size="small"
                                    variant="text"
                                    href={evidencia.archivo_url || evidencia.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ fontSize: '0.75rem', minWidth: 'auto' }}
                                  >
                                    Ver #{idx + 1}
                                  </Button>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
                                -
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Si no hay respuestas */}
      {(!respuestasPorCategoria || Object.keys(respuestasPorCategoria).length === 0) && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <DescriptionIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No hay respuestas registradas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Esta auditoría aún no tiene respuestas completadas.
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default AuditDetail;
